/**
 * ExtendBookingRow — one more block on a booking in progress, from the member's phone
 * (Simulator Companion slice 9).
 *
 * Sits under the check-in code row on both booking pages and decides for itself
 * whether it shows: the club runs the companion, the booking is confirmed and running
 * by the phone's clock, and the server has a block on offer. The server is the
 * authority on all three — this only avoids a read for a booking that is plainly not
 * in progress. The confirm sheet mirrors the cancel sheet beside it: the lines the
 * quote priced, the total, who pays, one button.
 *
 * A refusal is read back from the envelope, never thrown (internalapi-envelope):
 * a stale end re-reads the offer (someone extended first), a declined card says the
 * provider's words, a closed gap hides the row.
 */

import { useState } from "react";
import { asUtcDate, asTimezone, formatClubTime } from "@butter/library/datetime";
import { BottomSheet, butterToast } from "@butter/ui/patterns";
import { useNow } from "~/hooks/useNow";
import { useClubId } from "~/services/appRuntime";
import { useExtendBooking, useExtensionOfferQuery } from "~/services/internalApi/booking/hooks";

const MIN = 60_000;
/** How long past the end the row still asks: the club's end-of-session delay is at most ten minutes. */
const PAST_END_GRACE_MS = 10 * MIN;

export interface ExtendBookingRowProps {
  booking: {
    id: number;
    status: string;
    /** ISO 8601 */
    startTime: string;
    endTime: string;
  };
  /** The club's `simulatorCompanionEnabled` switch. */
  enabled: boolean;
  /** IANA; the new end is said in club time. */
  timezone?: string | null;
  testId: string;
}

const dollars = (n: number) => `$${n.toFixed(2)}`;

/**
 * How the extra time is paid, in the member's words (credits D9). The offer's credit block
 * says what account credit covers; `paymentMethodRequired` says a card share remains.
 */
function extensionPaymentCopy(offer: {
  paymentMethodRequired: boolean;
  credit?: { appliedCents: number; dueNowCents: number };
}): string | null {
  const applied = offer.credit?.appliedCents ?? 0;
  if (applied > 0 && !offer.paymentMethodRequired) return 'Paid with your account credit.';
  if (applied > 0 && offer.credit) {
    return `${dollars(applied / 100)} from account credit, ${dollars(offer.credit.dueNowCents / 100)} charged to the card on your account.`;
  }
  return offer.paymentMethodRequired ? 'Charged to the card on your account.' : null;
}

export function ExtendBookingRow({ booking, enabled, timezone, testId }: ExtendBookingRowProps) {
  const [clubIdRaw] = useClubId();
  const clubId = clubIdRaw ? parseInt(clubIdRaw, 10) : null;
  const [open, setOpen] = useState(false);

  // Read once a minute — a page left open across the start or the end re-decides.
  const now = useNow(MIN);
  const inWindow =
    booking.status === "confirmed"
    && new Date(booking.startTime).getTime() <= now
    && now < new Date(booking.endTime).getTime() + PAST_END_GRACE_MS;

  const offerQuery = useExtensionOfferQuery({ bookingId: booking.id, enabled: enabled && inWindow });
  const extend = useExtendBooking({ clubId });
  const offer = offerQuery.offer;
  if (!enabled || !inWindow || !offer) return null;

  const tz = asTimezone(timezone ?? "UTC");
  const total = offer.totalCost?.total ?? 0;
  const teamPays = offer.payerBreakdown?.team !== undefined && !offer.payerBreakdown.booker;
  const priceLabel = offer.basis === "covered" ? "Included in your plan" : teamPays ? `${dollars(offer.payerBreakdown!.team!.total)} to your team leaders` : dollars(total);
  const newEndLabel = formatClubTime(asUtcDate(new Date(offer.newEndTime)), tz);

  const confirm = async () => {
    const result = await extend.mutateAsync({ bookingId: booking.id, body: { expectedEndTime: offer.currentEndTime } });
    if (!result.success) {
      const code = result.error?.code;
      if (code === "CONFLICT") {
        // The end moved under us (someone extended first) or the gap closed: re-read, say so.
        butterToast.error(result.message || "This booking changed. Check the new end time.", "Couldn't add time");
        void offerQuery.refetch();
      } else {
        butterToast(result, "Couldn't add time");
      }
      setOpen(false);
      return;
    }
    const ended = formatClubTime(asUtcDate(new Date(result.payload.endTime)), tz);
    butterToast.success(`Your booking now ends at ${ended}.`, `${result.payload.blockMinutes} minutes added`);
    setOpen(false);
  };

  return (
    <>
      <div className="py-2 flex justify-between items-center gap-4" data-testid={testId}>
        <div>
          <p className="mb-1 text-sm font-medium text-fg-primary">Add {offer.blockMinutes} minutes</p>
          <p className="text-xs text-fg-quaternary">
            {priceLabel} · your booking would end at {newEndLabel}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={extend.isPending}
          data-testid={`${testId}-button`}
          className="whitespace-nowrap rounded-full border border-theme-600 bg-white px-5 py-2 text-sm font-medium text-fg-primary transition-colors hover:bg-theme-50 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 disabled:opacity-50"
        >
          Extend
        </button>
      </div>

      <BottomSheet
        open={open}
        onOpenChange={setOpen}
        title={`Add ${offer.blockMinutes} minutes`}
        description={`Your booking would end at ${newEndLabel}.`}
        isBusy={extend.isPending}
        className="mx-auto max-w-lg"
      >
        <div className="space-y-3 pt-2" data-testid={`${testId}-sheet`}>
          {offer.fees.length > 0 && (
            <ul className="divide-y divide-gray-100 text-sm">
              {offer.fees.map((fee, i) => (
                <li key={i} className="flex justify-between py-2">
                  <span className="text-fg-tertiary">{fee.label}</span>
                  <span className="font-medium text-fg-primary">{dollars(fee.baseAmount)}</span>
                </li>
              ))}
              {offer.totalCost && offer.totalCost.taxAmount > 0 && (
                <li className="flex justify-between py-2">
                  <span className="text-fg-tertiary">Tax</span>
                  <span className="font-medium text-fg-primary">{dollars(offer.totalCost.taxAmount)}</span>
                </li>
              )}
              {offer.totalCost?.processingFee !== undefined && offer.totalCost.processingFee > 0 && (
                <li className="flex justify-between py-2">
                  <span className="text-fg-tertiary">Processing fee</span>
                  <span className="font-medium text-fg-primary">{dollars(offer.totalCost.processingFee)}</span>
                </li>
              )}
              <li className="flex justify-between py-2 text-base">
                <span className="font-semibold text-fg-primary">Total</span>
                <span className="font-semibold text-fg-primary" data-testid={`${testId}-total`}>{priceLabel}</span>
              </li>
            </ul>
          )}
          {offer.basis === "covered" && (
            <p className="text-sm text-fg-tertiary">The extra time is covered by your plan.</p>
          )}
          {teamPays ? (
            <p className="text-sm text-fg-tertiary">Your team leaders will be charged for the extra time, as they are for overage.</p>
          ) : extensionPaymentCopy(offer) ? (
            <p className="text-sm text-fg-tertiary" data-testid={`${testId}-payment-copy`}>{extensionPaymentCopy(offer)}</p>
          ) : null}
          <div className="flex gap-4 pt-2">
            <button
              type="button"
              onClick={() => void confirm()}
              disabled={extend.isPending}
              data-testid={`${testId}-confirm`}
              className="btn btn-primary btn-xl flex-1"
            >
              {extend.isPending ? "Adding time..." : `Add ${offer.blockMinutes} minutes`}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={extend.isPending}
              className="btn btn-secondary btn-xl flex-1"
            >
              Not now
            </button>
          </div>
        </div>
      </BottomSheet>
    </>
  );
}
